import { LoadingArea } from '@consigli/facade';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureFlag, useFeatureFlag, useFeatureFlagsReady } from '@/feature-flags';
import { PageNotFound } from '@/layouts/page-not-found';

import { AgentsDirectoryPage } from './agents-directory-page';

/**
 * Gates `/agents` on the redesign flag.
 *
 * There is no older page behind this URL — the directory is new — so the off
 * state is a 404, as the router's splat would answer it if the route did not
 * exist. Bare, not inside the shell: with the flag off root-layout leaves the
 * legacy PageHeader up, and this is a page the user was never offered.
 *
 * Waits for `useFeatureFlagsReady` rather than reading the flag alone: an unread
 * flag resolves to `false`, so rendering immediately would paint a 404 and then
 * swap it to the page once the first fetch lands.
 *
 * Flag-lifetime code. It goes when the old pages do, and this becomes the page.
 */
export const AgentsDirectoryRoute: FC = () => {
  const { t } = useTranslation();
  const ready = useFeatureFlagsReady();
  const redesigned = useFeatureFlag(FeatureFlag.AppRedesign);

  if (!ready) {
    return (
      <div className="flex justify-center items-center">
        <LoadingArea title={t('agents-directory.loading-title')} className="mx-auto mt-[25vh]" />
      </div>
    );
  }

  return redesigned ? <AgentsDirectoryPage /> : <PageNotFound />;
};
